import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  Lightning, CheckCircle, X, ArrowSquareOut, Warning, CurrencyDollar,
  CreditCard, Receipt, Copy, QrCode, ShieldCheck,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { formatBRL, formatDate } from "@/lib/format";

const STATUS_LABEL = {
  authorized: { label: "Ativo", color: "emerald" },
  pending: { label: "Aguardando autorização", color: "amber" },
  cancelled: { label: "Cancelado", color: "rose" },
  paused: { label: "Pausado", color: "zinc" },
};

export const PaymentCard = ({ planValue, dueDate, onChanged }) => {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [openPayDialog, setOpenPayDialog] = useState(false);
  const [paymentLink, setPaymentLink] = useState(null);
  const [linkLoading, setLinkLoading] = useState(false);
  const [pixData, setPixData] = useState(null);
  const [pixLoading, setPixLoading] = useState(false);
  const [mode, setMode] = useState("pix"); // 'pix' | 'link'
  const [polling, setPolling] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/client/billing/status"); setStatus(data); }
    catch {}
  };

  useEffect(() => {
    load();
    const params = new URLSearchParams(window.location.search);
    if (params.has("payment")) {
      const r = params.get("payment");
      if (r === "success") toast.success("Pagamento aprovado! Em instantes atualizamos seu plano.");
      else if (r === "pending") toast.info("Pagamento pendente. Aguardando confirmação do Mercado Pago.");
      else if (r === "failure") toast.error("Pagamento não aprovado. Tente novamente.");
      window.history.replaceState({}, "", window.location.pathname);
      setTimeout(load, 2500);
    }
    if (params.has("autopay")) {
      toast.success("Pagamento automático configurado! Aguardando autorização do Mercado Pago.");
      window.history.replaceState({}, "", window.location.pathname);
      setTimeout(load, 1800);
    }
  }, []);

  // Poll PIX status every 6s while dialog open
  useEffect(() => {
    if (!pixData?.payment_id || !openPayDialog || mode !== "pix") return;
    setPolling(true);
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/client/payments/pix/${pixData.payment_id}`);
        if (data.status && data.status !== pixData.status) {
          setPixData((p) => ({ ...p, status: data.status }));
          if (data.status === "approved") {
            toast.success("Pagamento aprovado! 🎉 Seu plano foi renovado.");
            clearInterval(t); setPolling(false);
            setTimeout(() => { setOpenPayDialog(false); load(); onChanged?.(); }, 1500);
          } else if (["cancelled", "rejected"].includes(data.status)) {
            toast.error("Pagamento não aprovado.");
            clearInterval(t); setPolling(false);
          }
        }
      } catch { /* keep polling */ }
    }, 6000);
    return () => { clearInterval(t); setPolling(false); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pixData?.payment_id, openPayDialog, mode]);

  if (!status) return null;
  if (!status.mp_enabled) return null;

  const stMeta = STATUS_LABEL[status.preapproval_status] || null;
  const isActive = status.preapproval_status === "authorized" && status.auto_renew;
  const isPending = status.preapproval_status === "pending";

  const activateAutoRenew = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/client/billing/preapproval");
      if (data.init_point) window.location.href = data.init_point;
      else toast.error("Falha ao gerar link de pagamento");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao ativar");
    } finally { setBusy(false); }
  };

  const cancelAutoRenew = async () => {
    if (!window.confirm("Tem certeza que deseja cancelar o pagamento automático?")) return;
    setBusy(true);
    try {
      await api.delete("/client/billing/preapproval");
      toast.success("Pagamento automático cancelado");
      load(); onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro");
    } finally { setBusy(false); }
  };

  const generatePix = async () => {
    setPixLoading(true); setPixData(null);
    try {
      const { data } = await api.post("/client/payments/create-pix", {});
      setPixData(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao gerar PIX");
    } finally { setPixLoading(false); }
  };

  const generatePaymentLink = async () => {
    setLinkLoading(true); setPaymentLink(null);
    try {
      const { data } = await api.post("/client/billing/payment-link");
      setPaymentLink(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao gerar link");
    } finally { setLinkLoading(false); }
  };

  const openPayNow = () => {
    setOpenPayDialog(true);
    setMode("pix");
    setPaymentLink(null); setPixData(null);
    generatePix();
  };

  const switchMode = (next) => {
    setMode(next);
    if (next === "pix" && !pixData) generatePix();
    if (next === "link" && !paymentLink) generatePaymentLink();
  };

  const copyPixCode = async () => {
    if (!pixData?.qr_code) return;
    try {
      await navigator.clipboard.writeText(pixData.qr_code);
      toast.success("Código PIX copiado! Cole no app do seu banco.");
    } catch {
      toast.error("Não foi possível copiar. Selecione manualmente.");
    }
  };

  return (
    <>
      <Card
        className={`border rounded-2xl p-5 card-soft ${isActive ? "bg-emerald-50/40 border-emerald-200" : "bg-white border-zinc-200"}`}
        data-testid="payment-card"
      >
        <div className="flex items-start gap-4">
          <div className={`w-11 h-11 rounded-xl grid place-items-center flex-shrink-0 ${isActive ? "bg-emerald-100 text-emerald-700" : "bg-blue-50 text-blue-700"}`}>
            {isActive ? <CheckCircle size={22} weight="fill"/> : <CreditCard size={22} weight="fill"/>}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="font-display text-lg font-semibold">Pagamento</div>
              {stMeta && (
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium border bg-${stMeta.color}-50 text-${stMeta.color}-700 border-${stMeta.color}-200`}>
                  {stMeta.label}
                </span>
              )}
            </div>
            <div className="text-sm text-zinc-600 mt-0.5">
              {planValue > 0
                ? <>Valor do plano: <strong>{formatBRL(planValue)}</strong>{dueDate && <> · Vence em {formatDate(dueDate)}</>}</>
                : "Valor do plano não configurado"}
            </div>
          </div>
        </div>

        {/* Two payment options */}
        {!isActive && (
          <div className="mt-5 grid sm:grid-cols-2 gap-3">
            {/* Pay now (one-time) */}
            <button
              onClick={openPayNow}
              disabled={!planValue}
              data-testid="pay-now-btn"
              className="group text-left p-4 rounded-xl border-2 border-blue-200 bg-blue-50/50 hover:border-blue-400 hover:bg-blue-50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <div className="flex items-start justify-between">
                <div className="w-9 h-9 rounded-lg bg-blue-600 text-white grid place-items-center">
                  <QrCode size={18} weight="fill"/>
                </div>
                <span className="text-[10px] tracking-widest uppercase font-semibold text-blue-700">Rápido</span>
              </div>
              <div className="font-display text-base font-semibold mt-3">Pagar agora</div>
              <div className="text-xs text-zinc-600 mt-1">Pix, cartão ou boleto via Mercado Pago. Confirma na hora.</div>
            </button>

            {/* Auto-renew */}
            <button
              onClick={activateAutoRenew}
              disabled={busy || !planValue}
              data-testid="autopay-activate"
              className="group text-left p-4 rounded-xl border-2 border-amber-200 bg-amber-50/40 hover:border-amber-400 hover:bg-amber-50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <div className="flex items-start justify-between">
                <div className="w-9 h-9 rounded-lg bg-amber-500 text-white grid place-items-center">
                  <Lightning size={18} weight="fill"/>
                </div>
                <span className="text-[10px] tracking-widest uppercase font-semibold text-amber-700">Recomendado</span>
              </div>
              <div className="font-display text-base font-semibold mt-3">Pagamento automático</div>
              <div className="text-xs text-zinc-600 mt-1">Cobrança recorrente 1-clique. Nunca mais atrase.</div>
            </button>
          </div>
        )}

        {isActive && (
          <div className="mt-4 space-y-3">
            <div className="flex items-start gap-2 text-sm text-emerald-800 bg-emerald-100/50 rounded-lg p-3">
              <ShieldCheck size={16} weight="fill" className="mt-0.5 flex-shrink-0"/>
              <span>Seu plano renova automaticamente. <strong>Sem stress, sem atraso.</strong></span>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Button onClick={openPayNow} disabled={!planValue} variant="outline" size="sm" data-testid="pay-now-btn">
                <QrCode className="mr-1.5" size={14}/> Pagar avulso
              </Button>
              <Button onClick={cancelAutoRenew} variant="outline" size="sm" data-testid="autopay-cancel" className="text-rose-700 border-rose-200 hover:bg-rose-50">
                <X className="mr-1.5" size={14} weight="bold"/> Cancelar autopay
              </Button>
            </div>
          </div>
        )}

        {isPending && (
          <div className="mt-4">
            <a
              href={status.init_point}
              target="_blank" rel="noreferrer"
              data-testid="autopay-resume"
              className="inline-flex items-center gap-2 text-sm font-medium text-blue-600 hover:underline"
            >
              Continuar autorização no Mercado Pago <ArrowSquareOut size={14}/>
            </a>
          </div>
        )}

        {!planValue && (
          <div className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2 flex items-center gap-2">
            <Warning size={14} weight="fill"/> Seu plano ainda não tem valor configurado. Fale com o suporte.
          </div>
        )}
      </Card>

      {/* Pay Now Dialog */}
      <Dialog open={openPayDialog} onOpenChange={setOpenPayDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <QrCode size={20} weight="duotone" className="text-blue-600"/>
              Pagar agora
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="rounded-xl bg-zinc-50 border border-zinc-200 p-4 text-center">
              <div className="text-[11px] tracking-widest uppercase text-zinc-500 font-semibold">Valor a pagar</div>
              <div className="metric-num text-3xl mt-2">{formatBRL(pixData?.amount ?? paymentLink?.amount ?? planValue)}</div>
              {pixData?.discount_pct > 0 && (
                <div className="text-xs text-emerald-700 mt-1">
                  Desconto fidelidade {pixData.loyalty_tier?.toUpperCase()}: -{pixData.discount_pct}% (de {formatBRL(pixData.base_value)})
                </div>
              )}
            </div>

            {/* Mode toggle */}
            <div className="grid grid-cols-2 gap-2 p-1 bg-zinc-100 rounded-lg">
              <button
                onClick={() => switchMode("pix")}
                data-testid="pay-mode-pix"
                className={`h-9 rounded-md text-sm font-medium transition ${mode === "pix" ? "bg-white text-zinc-900 shadow-sm" : "text-zinc-500"}`}
              >PIX (instantâneo)</button>
              <button
                onClick={() => switchMode("link")}
                data-testid="pay-mode-link"
                className={`h-9 rounded-md text-sm font-medium transition ${mode === "link" ? "bg-white text-zinc-900 shadow-sm" : "text-zinc-500"}`}
              >Cartão / Boleto</button>
            </div>

            {mode === "pix" && (
              pixLoading ? (
                <div className="text-center py-8 text-zinc-500 text-sm">Gerando código PIX...</div>
              ) : pixData?.qr_code_base64 ? (
                <>
                  <div className="rounded-xl border border-zinc-200 bg-white p-4 grid place-items-center">
                    <img
                      src={`data:image/png;base64,${pixData.qr_code_base64}`}
                      alt="QR Code PIX"
                      data-testid="pix-qr-image"
                      className="w-56 h-56 object-contain"
                    />
                  </div>
                  <button
                    onClick={copyPixCode}
                    data-testid="pix-copy-btn"
                    className="w-full h-11 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-medium flex items-center justify-center gap-2"
                  >
                    <Copy size={16}/> Copiar código PIX (copia e cola)
                  </button>
                  <div className="text-xs text-zinc-500 text-center">
                    Abra o app do seu banco → PIX → Pagar com QR Code ou cole o código copiado.
                  </div>
                  {polling && pixData?.status === "pending" && (
                    <div className="flex items-center justify-center gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg py-2">
                      <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse"></span>
                      Aguardando pagamento... atualizamos automaticamente quando confirmado.
                    </div>
                  )}
                  {pixData?.status === "approved" && (
                    <div className="flex items-center justify-center gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg py-3 font-medium">
                      <CheckCircle weight="fill" size={18}/> Pagamento confirmado!
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-4 text-rose-600 text-sm">
                  Não foi possível gerar o PIX. Tente novamente ou use Cartão/Boleto.
                </div>
              )
            )}

            {mode === "link" && (
              linkLoading ? (
                <div className="text-center py-6 text-zinc-500 text-sm">Gerando link seguro do Mercado Pago...</div>
              ) : paymentLink?.init_point ? (
                <>
                  <div className="space-y-2">
                    <a
                      href={paymentLink.init_point}
                      target="_blank" rel="noreferrer"
                      data-testid="payment-link-open"
                      className="block w-full text-center h-11 leading-[44px] rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-medium transition"
                    >
                      <ArrowSquareOut weight="bold" className="inline mr-2 mb-0.5" size={16}/>
                      Abrir Mercado Pago e pagar
                    </a>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(paymentLink.init_point);
                        toast.success("Link copiado!");
                      }}
                      data-testid="payment-link-copy"
                      className="w-full h-10 rounded-lg border border-zinc-200 hover:bg-zinc-50 text-sm font-medium text-zinc-700 flex items-center justify-center gap-2"
                    >
                      <Copy size={14}/> Copiar link de pagamento
                    </button>
                  </div>
                  <ul className="space-y-2 text-xs text-zinc-600">
                    <li className="flex items-start gap-2">
                      <CheckCircle weight="fill" className="text-emerald-600 mt-0.5 flex-shrink-0" size={14}/>
                      Pague com <strong>cartão</strong> ou <strong>boleto</strong>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle weight="fill" className="text-emerald-600 mt-0.5 flex-shrink-0" size={14}/>
                      Você pode compartilhar este link com quem vai pagar por você
                    </li>
                  </ul>
                </>
              ) : (
                <div className="text-center py-4 text-rose-600 text-sm">Não foi possível gerar o link. Tente novamente.</div>
              )
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenPayDialog(false)}>Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PaymentCard;
